import { json } from "@remix-run/node";
import { useLoaderData, useParams } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { useCategoryPage } from "~/api/queries/useCategoryPage";
import { AcademiaLoader } from "~/components/AcademiaLoader";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";
import { buildMeta, getCompanyFromMatches, ogImageUrl } from "~/utils/meta-helpers";
import { serverFetchOutcome, serverFetchSafe } from "~/utils/server-fetch.server";

import { CategoryHero } from "../components/CategoryHero";
import { CategoryNavigationButtons } from "../components/CategoryNavigationButtons";
import { FeaturedCourseShowcase } from "../components/FeaturedCourseShowcase";
import { MockupSection } from "../components/MockupSection";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { GetAvailableCoursesResponse, GetCategoryPageResponse } from "~/api/generated-api";

/**
 * Loader de SERVIDOR. Reemplaza al placeholder que devolvía `{ category: null }` y hacía dos
 * daños a la vez: cortaba el SSR de la página entera (ruta con `clientLoader` y sin `loader`
 * ⇒ Remix borra su `loaderData` ⇒ React Router sube al `HydrateFallback` de root) y dejaba
 * el `meta` mintiendo — el título OG caía siempre al genérico "Categoría | …".
 *
 * Dos fetch en paralelo, los mismos que las queries del componente:
 * `categoryPageQueryOptions` y `availableCoursesQueryOptions({ category: slug })`.
 * Anónimos a propósito: es lo que ve un crawler y es lo que se puede cachear por URL.
 */
export const loader = async ({ params }: LoaderFunctionArgs) => {
  const slug = params.slug || "";
  const encoded = encodeURIComponent(slug);

  const [categoryOutcome, coursesResponse] = await Promise.all([
    serverFetchOutcome<GetCategoryPageResponse>(`/api/category/page/${encoded}`),
    serverFetchSafe<GetAvailableCoursesResponse>(
      `/api/course/available-courses?page=1&perPage=100&language=es&category=${encoded}`,
    ),
  ]);

  const courses = coursesResponse?.data ?? null;

  // Sin categoría el status depende de POR QUÉ falta, igual que en CourseDetail:
  //   · 4xx de la API → la categoría no tiene landing pública → 404 REAL. Servir el cartel
  //     de "no encontrada" con 200 es un soft-404 y le dice al crawler que la URL es buena.
  //     `json(..., { status })` renderiza el componente igual (React Router toma el status
  //     más profundo distinto de 200), así que el humano ve la misma pantalla de siempre.
  //   · La API no contestó → 503, no 404: un blip no puede borrar la categoría del índice.
  //
  // OJO con el 404 de acá: la API resuelve esta URL sólo para categorías con
  // `showInMenu = true`. `ai-from-zero` no lo tiene, así que su página no existe — y por eso
  // el sitemap de `server.js` filtra por el MISMO criterio. Si alguien la vuelve a listar
  // sin prenderle `showInMenu`, el sitio publica una URL que él mismo contesta con 404.
  if (!categoryOutcome.ok) {
    return json(
      { category: null as GetCategoryPageResponse["data"] | null, courses },
      { status: categoryOutcome.reason === "not-found" ? 404 : 503 },
    );
  }

  return json({ category: categoryOutcome.data.data ?? null, courses });
};

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const company = getCompanyFromMatches(matches);
  const category = data?.category;

  const lang = "es";
  const heroTitle =
    category?.heroTitle && typeof category.heroTitle === "object"
      ? (category.heroTitle as Record<string, string>)[lang] ||
        Object.values(category.heroTitle)[0] ||
        ""
      : "";

  // Ahora que el loader trae la categoría de verdad, el título cae en cascada: el hero
  // configurado desde el admin > el nombre de la categoría > el genérico. Antes SIEMPRE caía
  // al genérico, porque el placeholder devolvía `null` y `meta` no veía nada.
  const displayTitle = heroTitle || (category?.title ? getCategoryTitle(category.title, lang) : "");

  return buildMeta({
    title: displayTitle ? `${displayTitle} | ${company}` : `Categoría | ${company}`,
    image: ogImageUrl(category?.heroImageUrl),
  });
};

function getCategoryTitle(
  title: string | Record<string, string> | object,
  language: string,
): string {
  if (typeof title === "string") return title;
  const record = title as Record<string, string>;
  return record?.[language] || record?.es || Object.values(record || {})[0] || "";
}

export default function CategoryLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { language } = useLanguageStore();

  // Siembra del loader: `ssr*` viaja en `window.__remixContext`, así que servidor y primer
  // render del cliente pintan lo mismo. Las queries ganan apenas resuelven.
  const { category: ssrCategory, courses: ssrCourses } = useLoaderData<typeof loader>();

  const { data: fetchedCategory, isLoading: isLoadingCategory } = useCategoryPage(slug ?? "");
  const categoryPage = fetchedCategory ?? ssrCategory ?? undefined;

  const categoryTitle = categoryPage?.title ? getCategoryTitle(categoryPage.title, language) : "";

  const { data: fetchedCourses, isLoading: isLoadingCourses } = useAvailableCourses({
    language,
    ...(slug && { category: slug }),
  });
  const courses = fetchedCourses ?? ssrCourses ?? undefined;

  // El loader de carga sólo si NO hay nada que mostrar.
  const isLoading = (isLoadingCategory || isLoadingCourses) && !categoryPage;

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <AcademiaLoader label={t("landing.categoryPage.loading")} />
      </div>
    );
  }

  if (!categoryPage) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("landing.categoryPage.notFound")}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CategoryHero
        heroImageUrl={categoryPage.heroImageUrl}
        heroTitle={categoryPage.heroTitle}
        heroSubtitle={categoryPage.heroSubtitle}
        heroCtaText={categoryPage.heroCtaText}
        heroCtaUrl={categoryPage.heroCtaUrl}
        heroOverlayColor={categoryPage.heroOverlayColor}
        language={language}
      />
      <MockupSection variant="value-proposition" />
      <FeaturedCourseShowcase courses={courses ?? []} language={language} />
      <CategoryNavigationButtons categoryTitle={categoryTitle} categorySlug={slug ?? ""} />
      <MockupSection variant="testimonials" />
      <MockupSection variant="cta-final" />
    </div>
  );
}
